import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info, Zap } from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const toast = useCallback((message, type = 'info', duration = 4000) => {
        const id = Math.random().toString(36).substr(2, 9);
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, duration);
    }, []);

    toast.success = (msg, duration) => toast(msg, 'success', duration);
    toast.error = (msg, duration) => toast(msg, 'error', duration);
    toast.info = (msg, duration) => toast(msg, 'info', duration);
    toast.zap = (msg, duration) => toast(msg, 'zap', duration);

    const remove = (id) => setToasts(prev => prev.filter(t => t.id !== id));

    return (
        <ToastContext.Provider value={toast}>
            {children}
            {/* Toast Container */}
            <div className="fixed top-6 right-6 z-[1000] flex flex-col gap-3 max-w-sm w-full">
                {toasts.map(t => (
                    <ToastItem key={t.id} t={t} onRemove={() => remove(t.id)} />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

const ToastItem = ({ t, onRemove }) => {
    const icons = {
        success: <CheckCircle className="text-emerald-400" size={20} />,
        error: <AlertCircle className="text-red-400" size={20} />,
        info: <Info className="text-purple-400" size={20} />,
        zap: <Zap className="text-yellow-400" size={20} />
    };

    const colors = {
        success: 'border-emerald-500/20 bg-emerald-500/5',
        error: 'border-red-500/20 bg-red-500/5',
        info: 'border-purple-500/20 bg-purple-500/5',
        zap: 'border-yellow-500/20 bg-yellow-500/5'
    };

    return (
        <div className={`
            group flex items-center gap-4 p-4 rounded-2xl border backdrop-blur-xl shadow-2xl
            animate-slide-in-right transform transition-all hover:scale-[1.02]
            ${colors[t.type] || colors.info}
        `}>
            <div className="shrink-0">{icons[t.type] || icons.info}</div>
            <div className="flex-1 text-sm font-medium text-neutral-200">
                {t.message}
            </div>
            <button
                onClick={onRemove}
                className="shrink-0 text-neutral-600 hover:text-white transition"
            >
                <X size={16} />
            </button>
            <div className="absolute bottom-0 left-0 h-1 bg-white/10 w-full overflow-hidden rounded-b-2xl">
                <div className="h-full bg-current opacity-20 animate-toast-progress" />
            </div>

            <style jsx="true">{`
                @keyframes slide-in-right {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .animate-slide-in-right {
                    animation: slide-in-right 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                @keyframes toast-progress {
                    from { width: 100%; }
                    to { width: 0%; }
                }
                .animate-toast-progress {
                    animation: toast-progress 4s linear forwards;
                }
            `}</style>
        </div>
    );
};
